/**
 * Ant Enhanced Color Palette
 * Derived from Tailwind's Slate color scale with professional blue accents
 */
export const COLORS = {
  // Background colors
  background: {
    page: '#f8fafc', // bg-slate-50 - Main page background
    card: '#ffffff', // bg-white - Cards, panels, header
    sidebar: '#0f172a', // bg-slate-900 - Dark sidebar
    tableHeader: '#f8fafc', // bg-slate-50 - Table headers
    input: '#ffffff', // Input fields
    hover: '#f1f5f9', // bg-slate-100 - Hover states
  },

  // Text colors
  text: {
    primary: '#1e293b', // text-slate-800 - Main text
    secondary: '#64748b', // text-slate-500 - Secondary text
    disabled: '#94a3b8', // text-slate-400 - Disabled/muted text
    inverse: '#ffffff', // White text on dark backgrounds
  },

  // Brand/Accent colors
  brand: {
    primary: '#2563eb', // bg-blue-600 - Primary action color
    primaryHover: '#1d4ed8', // bg-blue-700 - Primary hover
    primaryLight: '#dbeafe', // bg-blue-100 - Light primary background
    primaryBorder: '#93c5fd', // bg-blue-300 - Primary border color
  },

  // Semantic colors
  semantic: {
    success: '#059669', // text-emerald-600
    successBg: '#d1fae5', // bg-emerald-100
    warning: '#faad14', // Standard warning orange
    warningBg: '#fef3c7', // bg-amber-100
    error: '#ef4444', // text-red-500
    errorBg: '#fee2e2', // bg-red-100
    info: '#2563eb', // Same as primary
    infoBg: '#dbeafe',
  },

  // Border colors
  border: {
    default: '#e2e8f0', // border-slate-200
    light: '#f1f5f9', // border-slate-100
    dark: '#cbd5e1', // border-slate-300 (for emphasis)
  },

  // Sidebar specific
  sidebar: {
    background: '#0f172a', // bg-slate-900
    itemBg: 'transparent',
    itemHoverBg: '#1e293b', // bg-slate-800
    itemSelectedBg: '#2563eb', // bg-blue-600
    itemText: '#cbd5e1', // text-slate-300 (improved contrast)
    itemActiveText: '#ffffff',
  },
} as const;

/**
 * Midnight Pro - Dark Mode Palette
 * Deep navy surfaces with crisp text and cyan accents
 */
export const COLORS_DARK = {
  // Background colors
  background: {
    page: '#181C21', // Main app background
    card: '#1D242E', // Cards, panels, modal surfaces
    sidebar: '#181C21', // Sidebar background
    tableHeader: '#1D242E', // Table header surface
    input: '#1D242E', // Input/select background
    hover: '#242D38', // Hover/raised row surface
  },

  // Text colors
  text: {
    primary: '#E6EAF0', // Main text on dark surfaces
    secondary: '#A9B0B9', // Secondary text
    disabled: '#8F96A3', // Disabled text
    inverse: '#FFFFFF', // Text on brand-colored surfaces
  },

  // Brand/Accent colors
  brand: {
    primary: '#2C74D6',
    primaryHover: '#5F8FE0',
    primaryLight: '#1D242E',
    primaryBorder: '#3D424B',
  },

  // Semantic colors
  semantic: {
    success: '#4CC976',
    successBg: 'rgba(76, 201, 118, 0.16)',
    warning: '#C9A92E',
    warningBg: 'rgba(201, 169, 46, 0.16)',
    error: '#C23E3E',
    errorBg: 'rgba(194, 62, 62, 0.16)',
    info: '#5F8FE0',
    infoBg: 'rgba(95, 143, 224, 0.16)',
  },

  // Border colors
  border: {
    default: '#3D424B',
    light: '#2A313B',
    dark: '#5C6168',
  },

  // Sidebar specific
  sidebar: {
    background: '#181C21',
    itemBg: 'transparent',
    itemHoverBg: '#1D242E',
    itemSelectedBg: '#1D242E',
    itemText: '#A9B0B9',
    itemActiveText: '#E6EAF0',
  },
} as const;

// Type exports for type-safe usage
export type ColorToken = typeof COLORS;
export type ColorTokenDark = typeof COLORS_DARK;
